import { useEffect, useMemo, useState } from 'react';
import type { PrayerTimings } from '../lib/types';
import {
  arcPos,
  computeDerivedTimes,
  findActiveRestrictedWindow,
  formatClock,
  formatRange,
  minToHH,
  toMin,
  PH_WINDOWS_I18N,
  type FardBuffers,
  type Madhab
} from '../lib/prayerWindows';
import { PRAYER_NAMES, useLocale } from '../lib/i18n';

/** Current minutes-of-day (with seconds fraction) in a given IANA timezone. */
function nowMinutesInTZ(tz?: string): number {
  const now = new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(now);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
    let h = get('hour');
    if (h === 24) h = 0; // some engines emit 24 at midnight
    return h * 60 + get('minute') + get('second') / 60;
  } catch {
    return now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  }
}

const SunIcon = () => (
  <svg className="ph-sky-icon ph-sky-sun" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="4.5" fill="#f5c842" stroke="#e6a817" strokeWidth=".8" />
    <g stroke="#f5c842" strokeWidth="1.5" strokeLinecap="round">
      <line x1="12" y1="1.5" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22.5" />
      <line x1="1.5" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22.5" y2="12" />
      <line x1="4.2" y1="4.2" x2="5.9" y2="5.9" />
      <line x1="18.1" y1="18.1" x2="19.8" y2="19.8" />
      <line x1="4.2" y1="19.8" x2="5.9" y2="18.1" />
      <line x1="18.1" y1="5.9" x2="19.8" y2="4.2" />
    </g>
  </svg>
);

const MoonIcon = () => (
  <svg className="ph-sky-icon ph-sky-moon" viewBox="0 0 24 24" fill="none">
    <path d="M20.5 13.5A8.5 8.5 0 0 1 10.5 3.5a7 7 0 1 0 10 10Z" fill="#c8d8e8" stroke="#a0b8cc" strokeWidth=".6" />
    <circle cx="18" cy="5" r=".8" fill="#e8eef4" />
    <circle cx="20" cy="8" r=".5" fill="#e8eef4" />
    <circle cx="16.5" cy="7.5" r=".4" fill="#e8eef4" />
  </svg>
);

export function DaysArc(props: {
  timings: PrayerTimings;
  timeFormat: '12h' | '24h';
  timeZone?: string;
  warnMadhab: Madhab;
  fardBuffers?: FardBuffers;
  /** When false, the arc renders the day's structure but no live now-indicator/warning/clock. */
  isToday?: boolean;
}) {
  const { locale, dir, copy } = useLocale();
  const isRTL = dir === 'rtl';
  const isToday = props.isToday !== false;
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isToday) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [isToday]);

  const derived = useMemo(
    () => computeDerivedTimes(props.timings, props.fardBuffers ?? { fajr: 15, asr: 15 }),
    [props.timings, props.fardBuffers]
  );

  const prayerNames = PRAYER_NAMES[locale] ?? PRAYER_NAMES.en;
  // Live "now" only matters for today; for other days the arc is a static shape.
  const nowMin = isToday ? nowMinutesInTZ(props.timeZone) : -1;
  const nextPrayer = isToday ? derived.prayers.find((p) => toMin(p.time) > nowMin) : undefined;
  const sunsetMin = toMin(derived.sunset);
  const fajrMin = toMin(derived.prayers[0].time);
  const isNight = nowMin >= sunsetMin || nowMin < fajrMin;

  const active = isToday ? findActiveRestrictedWindow(nowMin, derived.zones) : null;
  const nowClock = formatClock(minToHH(Math.floor(nowMin < 0 ? 0 : nowMin)), props.timeFormat);

  // Build warning text (madhab-aware label, localized window name).
  let warnText: { type: string; label: string; sec: string; ends: string } | null = null;
  if (active) {
    const type = active.strict ? copy.strict : props.warnMadhab === 'maliki' ? copy.makruhMaliki : copy.makruh;
    const zone = derived.zones.find((z) => z.key === active.key);
    const i18nWin = locale !== 'en' ? PH_WINDOWS_I18N[locale]?.[props.warnMadhab]?.[active.key] : undefined;
    const label = i18nWin?.label ?? zone?.label ?? '';
    const sec = locale === 'ar' ? (zone?.label ?? '') : (zone?.ar ?? '');
    const minsLeft = Math.ceil(active.toMin - nowMin);
    warnText = { type, label, sec, ends: `${copy.ends} ${minsLeft}${copy.m}` };
  }

  return (
    <div className="ph-day-arc">
      <div className="ph-arc-head">
        <div className="ph-arc-title">{copy.arc}</div>
        <div className="ph-arc-head-right">
          {isToday && <div className="ph-arc-now"><span>{nowClock}</span></div>}
        </div>
      </div>

      <div className="ph-arc-scroll">
        <div className="ph-arc-stage">
          <svg className="ph-arc-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="phDayGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" className="ph-arc-fill-stop-1" />
                <stop offset="100%" className="ph-arc-fill-stop-2" />
              </linearGradient>
              <linearGradient id="phArcCurveGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" className="ph-arc-curve-stop-1" />
                <stop offset="100%" className="ph-arc-curve-stop-2" />
              </linearGradient>
            </defs>
            <path className="ph-arc-fill" d="M 4 78 Q 50 -10 96 78 L 96 78 L 4 78 Z" fill="url(#phDayGrad)" />
            <line className="ph-arc-baseline" x1="0" y1="78" x2="100" y2="78" strokeWidth=".7" />
            <path className="ph-arc-curve-halo" d="M 4 78 Q 50 -10 96 78" fill="none" strokeWidth="2.2" strokeLinecap="round" />
            <path className="ph-arc-curve" d="M 4 78 Q 50 -10 96 78" stroke="url(#phArcCurveGrad)" strokeWidth="1" fill="none" strokeLinecap="round" strokeDasharray="0.9 1.4" />
          </svg>

          {/* Forbidden / makruh zones */}
          <div>
            {derived.zones.map((z) => {
              const leftPct = Math.min(arcPos(z.fromMin, derived.prayers, isRTL), arcPos(z.toMin, derived.prayers, isRTL));
              const rightPct = Math.max(arcPos(z.fromMin, derived.prayers, isRTL), arcPos(z.toMin, derived.prayers, isRTL));
              const w = rightPct - leftPct;
              if (w <= 0) return null;
              const strictLabel =
                z.key === 'sunrise' ? prayerNames.sunrise : z.key === 'sunset' ? (locale === 'en' ? 'Sunset' : prayerNames.maghrib) : z.label;
              const shortLabel = z.strict ? strictLabel : copy.makruhShort;
              const range = formatRange(minToHH(z.fromMin), minToHH(z.toMin), props.timeFormat);
              return (
                <div
                  key={z.key}
                  className={`ph-forbidden-zone ${z.strict ? 'is-strict' : 'is-makruh'}`}
                  data-key={z.key}
                  style={{ left: `${leftPct}%`, width: `${w}%` }}
                >
                  <span className="ph-forbidden-zone-label">{shortLabel}</span>
                  <span className="ph-forbidden-zone-time">{range}</span>
                </div>
              );
            })}
          </div>

          {/* Ishraq permissible marker */}
          <div>
            <div className="ph-arc-special is-ishraq" style={{ left: `${arcPos(toMin(derived.ishraq), derived.prayers, isRTL)}%`, bottom: 6 }}>
              <div className="ph-arc-special-dot" />
              <div className="ph-arc-special-label">{copy.ishraq.split(' ')[0]}</div>
            </div>
          </div>

          {/* Prayer markers */}
          <div className="ph-prayer-row">
            {derived.prayers.map((p) => {
              const x = arcPos(toMin(p.time), derived.prayers, isRTL);
              const isPast = toMin(p.time) <= nowMin;
              const isNext = nextPrayer?.key === p.key;
              return (
                <div
                  key={p.key}
                  className={`ph-marker ${isPast ? 'is-past' : ''} ${isNext ? 'is-next' : ''}`}
                  data-key={p.key}
                  style={{ left: `${x}%`, bottom: 6 }}
                >
                  <div className="ph-marker-dot" />
                  <div className="ph-marker-time">{formatClock(p.time, props.timeFormat)}</div>
                  <div className="ph-marker-label">{prayerNames[p.key] ?? p.name}</div>
                </div>
              );
            })}
          </div>

          {/* Now indicator (sun/moon) — live, today only */}
          {isToday && (
            <div className={`ph-now-indicator ${active ? 'is-forbidden' : ''}`} style={{ left: `${arcPos(nowMin, derived.prayers, isRTL)}%` }}>
              {!isNight ? <SunIcon /> : <MoonIcon />}
            </div>
          )}
        </div>
      </div>

      {warnText && (
        <div className="ph-nawafil-warn" style={{ display: 'flex' }}>
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>
            {warnText.type} — {warnText.label}
            {warnText.sec ? <span className="ph-warn-ar"> {warnText.sec}</span> : null}
          </span>
          <span className="ph-warn-countdown">{warnText.ends}</span>
        </div>
      )}

      <div className="ph-arc-legend">
        <div className="ph-arc-legend-item"><span className="ph-arc-legend-swatch is-strict" />{copy.strict}</div>
        <div className="ph-arc-legend-item"><span className="ph-arc-legend-swatch is-makruh" />{copy.makruh}</div>
        <div className="ph-arc-legend-item"><span className="ph-arc-legend-swatch is-ok" />{copy.ishraq}</div>
      </div>
    </div>
  );
}
